import {
  Autocomplete,
  Checkbox,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { User, UserGroup } from '@ulysses-inc/harami_api_client'
import { useFormik } from 'formik'
import type React from 'react'
import { useId, useState } from 'react'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import Yup from 'src/features/validation/yup'
import FooterButton from 'src/views/components/drawer/FooterButton'
import {
  useMutationAddUserGroup,
  useMutationUpdateUserGroup,
} from '../userGroups/api'

interface UserGroupForm {
  id?: number
  name: string
  userIds: string[]
}

interface Props {
  activeUserGroupId?: number
  userGroups: UserGroup[]
  users: User[]
  onCancel: () => void
  onSuccess: () => void
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required().label('ユーザーグループ名'),
})

const EditUserGroup: React.FC<Props> = ({
  activeUserGroupId,
  userGroups,
  users,
  onCancel,
  onSuccess,
}) => {
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar()

  const activeUserGroup = activeUserGroupId
    ? userGroups.find(({ id }) => id === activeUserGroupId)
    : undefined

  const addMutation = useMutationAddUserGroup()
  const updateMutation = useMutationUpdateUserGroup()

  const isLoading = addMutation.isPending || updateMutation.isPending

  const [selectedUsers, setSelectedUsers] = useState<User[]>(
    activeUserGroup?.users ?? [],
  )

  const userGroupNameFieldId = useId()
  const userGroupUsersFieldId = useId()

  const { setFieldValue, handleChange, handleSubmit, values, errors, touched } =
    useFormik<UserGroupForm>({
      initialValues: {
        id: activeUserGroup?.id,
        name: activeUserGroup?.name ?? '',
        userIds: activeUserGroup?.users?.map(({ id }) => `${id}`) ?? [],
      },
      validationSchema,
      onSubmit: async ({ id, name, userIds }, { resetForm }) => {
        const users =
          userIds?.map(strId => ({ id: Number(strId) }) as User) ?? []

        try {
          if (id) {
            await updateMutation.mutateAsync({
              userGroupId: id,
              userGroup: { id, name, users },
            })
            showSuccessSnackbar('ユーザーグループを更新しました')
          } else {
            await addMutation.mutateAsync({
              userGroup: { id: 0, name, users },
            })
            showSuccessSnackbar('ユーザーグループを追加しました')
          }
          resetForm()
          onSuccess()
        } catch {
          showErrorSnackbar(
            id
              ? 'ユーザーグループの更新に失敗しました'
              : 'ユーザーグループの追加に失敗しました',
          )
        }
      },
    })

  return (
    <Stack sx={{ minHeight: '100vh' }}>
      <Typography variant="h6" sx={{ my: 2, px: 3 }}>
        {`ユーザーグループの${activeUserGroup ? '編集' : '追加'}`}
      </Typography>
      <Divider />
      <Stack
        justifyContent="space-between"
        sx={{ display: 'flex', height: '100%' }}
      >
        <Stack sx={{ px: 3, py: 2, gap: 2 }}>
          <Stack>
            <Typography
              variant="subtitle1"
              sx={{ mb: 1 }}
              component="label"
              htmlFor={userGroupNameFieldId}
            >
              ユーザーグループ名
            </Typography>
            <TextField
              id={userGroupNameFieldId}
              fullWidth
              size="medium"
              onChange={handleChange('name')}
              value={values.name}
              error={Boolean(errors.name && touched.name)}
            />
            {errors.name && touched.name && (
              <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                {errors.name}
              </Typography>
            )}
          </Stack>

          <Stack>
            <Typography
              variant="subtitle1"
              sx={{ mb: 1 }}
              component="label"
              htmlFor={userGroupUsersFieldId}
            >
              所属させるユーザー
            </Typography>
            <Autocomplete<User, true, false, false>
              multiple
              id={userGroupUsersFieldId}
              disableCloseOnSelect
              options={users}
              getOptionLabel={option => option.name ?? '名称未設定'}
              value={selectedUsers}
              onChange={(_, newValue) => {
                setSelectedUsers(newValue)
                setFieldValue(
                  'userIds',
                  newValue
                    .filter(user => user.id != null)
                    .map(user => `${user.id}`),
                )
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option, { selected }) => {
                // eslint-disable-next-line react/prop-types
                const { key, ...rest } = props
                return (
                  <li key={key} {...rest}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      mr={1}
                    >
                      <Checkbox
                        size="small"
                        sx={{ mr: 1 }}
                        checked={selected}
                      />
                      {option.name}
                    </Stack>
                  </li>
                )
              }}
              slotProps={{
                chip: {
                  size: 'small',
                  variant: 'pale',
                },
              }}
              renderInput={params => <TextField {...params} />}
            />
          </Stack>
        </Stack>
        <Stack sx={{ px: 3, py: 2 }}>
          <FooterButton
            spinning={isLoading}
            onCancel={onCancel}
            onSubmit={handleSubmit}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}

export default EditUserGroup
